// components/admin/AdminProfile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dbSet, dbGet, dbOnValue } from '../../config/firebase';
import { 
  User, 
  Mail, 
  Phone, 
  Shield, 
  Calendar, 
  Save,
  Edit3,
  Camera,
  Key,
  Activity,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminProfile = () => {
  const { user, updateProfile, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'admin'
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Fetch recent activity (mock for demo)
    setActivityLog([
      { action: 'Logged in', time: new Date().toISOString(), type: 'login' },
      { action: 'Updated product catalog', time: new Date(Date.now() - 3600000).toISOString(), type: 'update' },
      { action: 'Processed order #ORD001', time: new Date(Date.now() - 7200000).toISOString(), type: 'order' },
      { action: 'Added new staff member', time: new Date(Date.now() - 86400000).toISOString(), type: 'staff' },
      { action: 'Updated settings', time: new Date(Date.now() - 172800000).toISOString(), type: 'settings' }
    ]);
  }, []);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setLoading(false);
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    toast.success('Password updated successfully');
    setShowPasswordChange(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'login': return <Activity size={16} />;
      case 'update': return <Edit3 size={16} />;
      case 'order': return <CheckCircle size={16} />;
      case 'staff': return <User size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getActivityColor = (type) => {
    switch(type) {
      case 'login': return '#3b82f6';
      case 'update': return '#8b5cf6';
      case 'order': return '#10b981';
      case 'staff': return '#f59e0b';
      default: return '#64748b';
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Profile Header Card */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        marginBottom: '24px'
      }}>
        {/* Cover */}
        <div style={{
          height: '140px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            bottom: '-50px',
            left: '40px',
            width: '100px',
            height: '100px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #2874f0, #1a5dc7)',
            border: '4px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.5rem',
            fontWeight: 700,
            color: 'white',
            boxShadow: '0 8px 25px rgba(40,116,240,0.3)'
          }}>
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        </div>

        {/* Profile Info */}
        <div style={{ padding: '60px 40px 30px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                {user?.name || 'Admin'}
              </h2>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                background: '#e3f2fd',
                color: '#1565c0',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                <Shield size={14} />
                {user?.role?.toUpperCase() || 'ADMIN'}
              </div>
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                <Mail size={14} style={{ display: 'inline', marginRight: '6px' }} />
                {user?.email}
              </p>
              {user?.phone && (
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
                  <Phone size={14} style={{ display: 'inline', marginRight: '6px' }} />
                  {user.phone}
                </p>
              )}
              <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>
                <Calendar size={14} style={{ display: 'inline', marginRight: '6px' }} />
                Joined {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-IN')}
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setEditing(!editing)}
                style={{
                  padding: '10px 20px',
                  background: editing ? '#10b981' : '#f1f5f9',
                  color: editing ? 'white' : '#334155',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s ease'
                }}
              >
                {editing ? <><Save size={16} /> Save</> : <><Edit3 size={16} /> Edit Profile</>}
              </button>
              <button
                onClick={() => setShowPasswordChange(true)}
                style={{
                  padding: '10px 20px',
                  background: '#fff3e0',
                  color: '#e65100',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s ease'
                }}
              >
                <Key size={16} /> Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Form */}
      {editing && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          marginBottom: '24px'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: '1.1rem', fontWeight: 600 }}>
            Edit Profile
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={inputStyle}
                placeholder="Admin name"
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={formData.email}
                disabled
                style={{ ...inputStyle, background: '#f1f5f9', cursor: 'not-allowed' }}
              />
            </div>
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={inputStyle}
                placeholder="Your phone number"
              />
            </div>
            <div>
              <label style={labelStyle}>Role</label>
              <input
                type="text"
                value={formData.role}
                disabled
                style={{ ...inputStyle, background: '#f1f5f9', cursor: 'not-allowed' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginBottom: '20px', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={20} color="#2874f0" /> Recent Activity
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {activityLog.map((activity, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: `${getActivityColor(activity.type)}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: getActivityColor(activity.type)
              }}>
                {getActivityIcon(activity.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{activity.action}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  {new Date(activity.time).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <>
          <div 
            onClick={() => setShowPasswordChange(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 500,
              backdropFilter: 'blur(4px)'
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '16px',
            zIndex: 600,
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              <Key size={20} style={{ display: 'inline', marginRight: '8px' }} />
              Change Password
            </h3>
            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Confirm Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowPasswordChange(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#2874f0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '0.9rem',
  transition: 'all 0.2s ease',
  outline: 'none'
};

export default AdminProfile;